import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import Button from 'components/shared/Button';
import { ReactComponent as IconUser } from 'images/user.svg';
import { Input } from 'components/shared/Input';
import useProfile from 'sdk/hooks/user/useProfile';
import useUpdateUser from 'sdk/hooks/user/useUpdateUser';
import useUser from 'sdk/hooks/user/useUser';
import useLocation from 'sdk/hooks/useLocation';
import { generateUsername } from 'sdk/utils/generateUsername';
import { StringConstant } from 'utils/Constants';
import styles from './SignUp.module.scss';
import usernameStyles from './Username.module.scss';

type UsernameViewProps = {
  handleNext: () => void,
  className?: string,
}

function UsernameView({
  handleNext,
  className,
}: UsernameViewProps) {
  const [username, setUsername] = useState<string>('')
  const [isEditEnabled, setIsEditEnabled] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [showInputError, setShowInputError] = useState(false)
  const userData = useUser()
  const locationData = useLocation()

  const profileData = useProfile(true)

  const getRandomInt = (max: number) => Math.floor(Math.random() * max)

  function getCookie(name: string) {
    const cookieName = `${name}=`;
    const cookies = document.cookie.split(';');

    const matchingCookie = cookies
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(cookieName));

    if (matchingCookie) {
      const value = matchingCookie.substring(cookieName.length);
      return value;
    }
    return null;
  }

  const createCookie = (cookieName: string, cookieValue: string) => {
    if (getCookie(cookieName) !== null) {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 10);
      document.cookie = `${cookieName} = ${cookieValue}; expires = ${date.toString()}`;
    }
  }

  useEffect(() => {
    if (profileData.isSuccess && profileData?.data?.email && !username) {
      const savedUsername = getCookie('username');
      if (savedUsername !== null && savedUsername !== '') {
        setUsername(savedUsername.toString())
      } else {
        const emailParts = profileData.data.email.split('@');
        const usernamePrefix = emailParts[0].length < 3
          ? generateUsername()
          : emailParts[0].toLowerCase();
        setUsername(usernamePrefix + getRandomInt(100));
      }
    } else {
      setUsername(generateUsername());
    }
  }, [profileData.isSuccess, profileData?.data?.email])

  useEffect(() => {
    if (username.length > 0) {
      createCookie('username', username);
    }
  }, [username])

  const updateUserMutation = useUpdateUser({
    id: userData?.data?.user?.id ?? '',
    username,
  })

  useEffect(() => {
    if (updateUserMutation.data) {
      // @ts-ignore
      if (analytics) { // @ts-ignore
        analytics.identify(updateUserMutation.data?.id, { // @ts-ignore
          username: updateUserMutation?.username,
          email: updateUserMutation.data?.email,
          phone: updateUserMutation.data?.phone,
          birthday: updateUserMutation.data?.birthday,
          country: locationData?.data?.location?.country,
          city: locationData?.data?.location?.city,
        });
      }
    }
  }, [updateUserMutation.data])

  useEffect(() => {
    if (updateUserMutation.isError) {
      const errorObj: any = updateUserMutation.error
      setError(StringConstant.SIGN_UP_ERROR_MEESAGE)
      setShowInputError(true)
    }
  }, [updateUserMutation.isError])

  if (updateUserMutation.isSuccess) {
    handleNext()
  }

  const handleUsernameChange = (e: React.FormEvent<HTMLInputElement>) => {
    setError('')
    setUsername(
      (e.target as HTMLInputElement)
        .value
        .replace(/[^\w-]/i, '')
        .toLowerCase(),
    )
  }

  const getError = () => {
    if (username.length < 3) return 'The username is too short'
    return error || ''
  }

  const onBlurInput = () => {
    setError(getError() || '')
    setShowInputError(Boolean(getError()))
  }

  return (
    <div className={className}>
      <div className={clsx(usernameStyles.usernameTitle, styles.header)}>
        {isEditEnabled ? 'Change username' : 'Your username'}
      </div>

      {isEditEnabled ? (
        <Input
          className={styles.phoneInput}
          value={username}
          onChange={handleUsernameChange}
          placeholder="Enter username"
          disabled={!isEditEnabled}
          icon={<IconUser />}
          onBlur={onBlurInput}
          error={showInputError}
          errorMessage={error}
          maxLength={400}
          white
          wide
        />
      ) : (
        <>
          <div className={usernameStyles.username}>{username}</div>

          <div className={usernameStyles.usernameDescription}>
            You can change your username
            <br />
            in settings
          </div>

          <div
            className={usernameStyles.enableEditLink}
            onClick={() => setIsEditEnabled(true)}
          >
            Change my username
          </div>
        </>
      )}

      {(error && !isEditEnabled) && (
      <div className={styles.errorMessage}>{error}</div>
      )}

      <Button
        className={styles.margin20}
        disabled={!username || Boolean(error) || updateUserMutation.isLoading}
        onClick={() => updateUserMutation.mutate()}
        loading={updateUserMutation.isLoading}
        wide
        darkGreen
      >
        Finish
      </Button>
    </div>
  )
}

export default UsernameView
